import { DAY, YEAR_DAYS, CANON_SPEED } from '@/sim/clock';

/**
 * Advancing a world across an absence — the mechanism, not the policy.
 *
 * `PERSISTENCE.md` proves the hard half: advancing `T0 → T1` in one call and
 * in any number of pieces produce bit-identical worlds, save/load boundaries
 * included. **This file is what that proof is FOR.** A catch-up has to be cut
 * into slices — nobody can block a browser for four minutes — and slicing is
 * only safe because partition invariance holds. `test/persistence-equivalence.ts`
 * is the thing that keeps this file correct; if it ever goes red, the stepper
 * below silently starts producing a different world from the one a player who
 * stayed connected would have had.
 *
 * Pure by law 7: no three, no `Math.random`, no wall clock. That last one is
 * load-bearing here rather than incidental — a catch-up is *about* real
 * elapsed time, and this module still never reads it. Elapsed comes in as a
 * parameter and the frame budget comes in as a predicate the caller owns, so
 * the simulation stays a pure function of world-time and the client keeps the
 * one fact that is genuinely a client fact.
 *
 * ## What an absence actually costs
 *
 * Two measured constants meet here. The clock runs at `CANON_SPEED` = 192
 * world-seconds per real second, so one world-year of history is 45.6 real
 * hours of being away. Catch-up runs at ~18 real seconds per world-year on
 * the shipped century at ~700 souls (`tools/persistence-soak.ts --profile`).
 *
 * So the ratio that decides this feature is about **1 : 9,000** — one second
 * of computing buys about two and a half hours of absence:
 *
 *     away for…      world time      catch-up costs
 *     an hour         8 world-days      ~0.4 s
 *     a day           192 world-days    ~9.5 s
 *     a week          3.7 world-years   ~66 s
 *     a month         16 world-years    ~4.8 min
 *     a year          192 world-years   ~58 min
 *
 * A day away is a progress bar. A week away is a long one. **A month away
 * cannot be paid at the door**, which is why `absenceSpan` takes a cap and
 * reports honestly when it bites, rather than quietly doing something else.
 */

/**
 * Real seconds of computation per world-year advanced, measured on the
 * shipped century at ~700 souls (`--world prehistory --years 25 --profile`:
 * 18.0 s/yr at year 1, 25.2 s/yr by year 25).
 *
 * Used ONLY to estimate — to size a cap and to tell a player what they are
 * waiting for. Nothing about the world's behaviour reads it, so a machine
 * that disagrees gets a wrong estimate and an identical world.
 */
export const REAL_SECONDS_PER_WORLD_YEAR = 18;

/** World-seconds in a world-year. */
export const WORLD_YEAR = DAY * YEAR_DAYS;

/**
 * The default ceiling on a single catch-up: **one world-year**, which is
 * about two real days of absence and about 18 seconds of waiting.
 *
 * This is a mechanism default, not a design ruling. What should happen when a
 * player returns after a month is a real question — the world falling behind
 * real time is one answer, a long wait is another, and a server advancing it
 * for you is a third — and `PERSISTENCE.md` records it as open. The cap is
 * here so that a caller who has not thought about it gets a bounded wait and
 * an honest `capped` flag rather than a frozen tab.
 *
 * **Where it bites: 1.90 real days away.** At 192x, one world-year is that
 * much absence, so anything longer advances less world-time than really
 * elapsed and the world falls behind the wall clock — permanently, since
 * nothing ever catches it back up. For a single-player world that may be
 * exactly right; for the shared world law 27 describes, it is not, because
 * two players would be in different centuries. `test/persistence-absence.ts`
 * asserts this boundary so that moving the default is a deliberate act.
 */
export const DEFAULT_CAP_WORLD_SECONDS = WORLD_YEAR;

export interface AbsenceEstimate {
  /** World-seconds to advance — already capped. */
  worldSeconds: number;
  worldYears: number;
  /** What the caller asked for before the cap, in world-seconds. */
  requestedWorldSeconds: number;
  /** True when the cap bit: the world will advance less than real time did. */
  capped: boolean;
  /** Rough real seconds this advance will take. Estimate only. */
  estimateRealSeconds: number;
}

/**
 * How much world-time an absence is worth, and what advancing it will cost.
 *
 * `realSecondsAway` is the caller's fact — measured against a wall-clock
 * stamp stored *beside* the save, never inside it, because when a client was
 * last open is not world history and a snapshot that carried it would stop
 * being reproducible.
 *
 * Negative and non-finite spans read as zero: a clock that went backwards
 * (timezone, NTP correction, a hand-edited stamp) must not rewind the world
 * or throw, and zero is the only safe reading of it.
 */
export function absenceSpan(
  realSecondsAway: number,
  opts: { speed?: number; capWorldSeconds?: number } = {},
): AbsenceEstimate {
  const speed = opts.speed ?? CANON_SPEED;
  const cap = Math.max(0, opts.capWorldSeconds ?? DEFAULT_CAP_WORLD_SECONDS);
  const away = Number.isFinite(realSecondsAway) ? Math.max(0, realSecondsAway) : 0;
  const requested = away * speed;
  const worldSeconds = Math.min(requested, cap);
  return {
    worldSeconds,
    worldYears: worldSeconds / WORLD_YEAR,
    requestedWorldSeconds: requested,
    capped: requested > cap,
    estimateRealSeconds: (worldSeconds / WORLD_YEAR) * REAL_SECONDS_PER_WORLD_YEAR,
  };
}

/** The narrowest thing this needs of a Simulation, so it couples to nothing. */
export interface Advanceable {
  advance(from: number, to: number): void;
}

export interface AbsenceRun {
  readonly from: number;
  readonly to: number;
  /** World-time reached so far. */
  readonly at: number;
  readonly remaining: number;
  readonly done: boolean;
  /** 0..1, and exactly 1 when done — safe to drive a progress bar with. */
  readonly progress01: number;
  /**
   * Advance by at most `worldSeconds`. Returns how much was actually
   * advanced, which is less than asked for on the final slice and 0 when
   * already finished.
   */
  step(worldSeconds: number): number;
  /**
   * Advance in slices while `hasBudget()` says there is time left, checked
   * BEFORE each slice so an exhausted budget does no work at all. Returns
   * true while the run has more to do.
   *
   * The predicate is where the wall clock lives — the caller's, never this
   * module's. A frame-budgeted client passes something like
   * `() => performance.now() < frameDeadline`; a headless tool passes
   * `() => true` and the run completes in one call.
   */
  pump(hasBudget: () => boolean, sliceWorldSeconds?: number): boolean;
}

/**
 * A catch-up in flight.
 *
 * Slice size is free — that is the whole point of the invariance proof — so
 * the default is one world-day, which is ~49 ms of work at the measured rate
 * and therefore about a frame's worth. A caller that wants smoother progress
 * can pass an hour; a caller that wants throughput can pass a year. All three
 * arrive at the identical world, and `test/persistence-absence.ts` asserts it
 * against a single uninterrupted `advance`.
 */
export function runAbsence(sim: Advanceable, from: number, to: number): AbsenceRun {
  // Both ends are sanitised, and `from` matters more than it looks. A NaN
  // `from` leaves `at` NaN, `at >= end` false and every `step` advancing by
  // NaN — so `pump` would spin for ever on a world clock that had already
  // gone wrong somewhere else. A run that cannot move must report itself
  // finished, not hang the tab.
  const start = Number.isFinite(from) ? from : 0;
  const end = Math.max(start, Number.isFinite(to) ? to : start);
  let at = start;

  const run: AbsenceRun = {
    get from() { return start; },
    get to() { return end; },
    get at() { return at; },
    get remaining() { return end - at; },
    get done() { return at >= end; },
    get progress01() {
      const span = end - start;
      return span > 0 ? Math.min(1, (at - start) / span) : 1;
    },
    step(worldSeconds: number): number {
      if (at >= end) return 0;
      const want = Number.isFinite(worldSeconds) ? worldSeconds : 0;
      if (!(want > 0)) return 0;
      const next = Math.min(end, at + want);
      sim.advance(at, next);
      const moved = next - at;
      at = next;
      return moved;
    },
    pump(hasBudget: () => boolean, sliceWorldSeconds = DAY): boolean {
      const slice = Number.isFinite(sliceWorldSeconds) && sliceWorldSeconds > 0
        ? sliceWorldSeconds : DAY;
      while (at < end && hasBudget()) run.step(slice);
      return at < end;
    },
  };
  return run;
}
